import React, { useState } from 'react'
import {
    Box,
    Container,
    Flex,
    FormControl,
    FormLabel,
    Textarea,
    VStack,
    Button,
    Card,
    CardHeader,
    CardBody,
    Text,
    Heading,
    Icon,
    Divider,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useBreakpointValue,
    useDisclosure,
    SimpleGrid,
    Badge,
    useToast
  } from '@chakra-ui/react';
import { AddIcon, InfoIcon, HamburgerIcon, ArrowBackIcon, WarningIcon, CheckIcon } from '@chakra-ui/icons';
import { ModernSidebar, UserMenu, BreadcrumbNav } from './SharedComponents';
import { validateGoalForm } from '../../utils/validation';
import toastService from '../../utils/toastService';

const CreateGoalPage = ({ state, onRouteChange, getOverview, onFormTextChange, onCreateJourney, toggleSidebar, setLoading, onRandomChapter }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, lg: false })

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Mobile Navigation */}
      {isMobile && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent bg="brand.700">
            <DrawerCloseButton color="white" />
            <DrawerHeader color="brand.600" fontSize="xl" fontWeight="bold">
              Grow
            </DrawerHeader>
            <DrawerBody>
              <ModernSidebar
                state={state}
                onRouteChange={onRouteChange} 
                onClose={onClose}
                toggleSidebar={toggleSidebar}
                onRandomChapter={onRandomChapter}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
      
      <Flex>
        {/* Desktop Sidebar */}
        {!isMobile && !state.sidebarCollapsed && (
          <Box 
            w="280px" 
            bg="white" 
            minH="100vh" 
            position="fixed" 
            left={0} 
            top={0}
            zIndex={10}
            borderRight="1px"
            borderColor="gray.200"
            boxShadow="sm"
            transition="all 0.3s ease"
          >
            <ModernSidebar
              state={state}
              onRouteChange={onRouteChange}
              toggleSidebar={toggleSidebar}
              onRandomChapter={onRandomChapter}
            />
          </Box>
        )}
        
        {/* Main Content */}
        <Box flex={1} ml={isMobile ? 0 : state.sidebarCollapsed ? 0 : "280px"} transition="margin-left 0.3s ease">
          {/* Top Navigation */}
          <Box bg="white" borderBottom="1px" borderColor="gray.200" px={{ base: 3, md: 6 }} py={{ base: 3, md: 4 }}>
            <Flex align="center" justify="space-between" gap={2}>
              <HStack spacing={{ base: 2, md: 4 }} flex="1" minW={0}>
                {isMobile && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={onOpen}
                    variant="ghost"
                    colorScheme="brand"
                    size="sm"
                    flexShrink={0}
                  />
                )}
                {/* Hamburger button when sidebar is collapsed on desktop */}
                {!isMobile && state.sidebarCollapsed && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={toggleSidebar}
                    variant="ghost"
                    color="brand.600"
                    _hover={{
                      color: 'brand.700',
                      bg: 'brand.50'
                    }}
                    transition="all 0.3s ease"
                  />
                )}
                <IconButton
                  icon={<ArrowBackIcon />}
                  variant="ghost"
                  colorScheme="brand"
                  onClick={() => onRouteChange('dashboard')}
                  aria-label="Back to Dashboard"
                  size="sm"
                  display={{ base: 'flex', md: 'none' }}
                  flexShrink={0}
                />
                <Button
                  leftIcon={<ArrowBackIcon />}
                  variant="ghost"
                  colorScheme="brand"
                  onClick={() => onRouteChange('dashboard')}
                  display={{ base: 'none', md: 'flex' }}
                >
                  Back to Dashboard
                </Button>
                <Heading size={{ base: 'xs', md: 'lg' }} color="gray.800" noOfLines={1} display={{ base: 'none', sm: 'block' }}>
                  Create New Goal
                </Heading>
              </HStack>

              <HStack spacing={{ base: 1, md: 4 }} flexShrink={0}>
                <UserMenu onRouteChange={onRouteChange} username={state.username} />
              </HStack>
            </Flex>
          </Box>
          
          {/* Breadcrumb Navigation */}
          <BreadcrumbNav 
            route={state.route} 
            onRouteChange={onRouteChange} 
          />
          
          {/* Form Content */}
          <Container maxW="4xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
            <VStack spacing={8} align="stretch">
              <GoalTemplates onFormTextChange={onFormTextChange} />
              <CreateGoalForm
                state={state}
                onFormTextChange={onFormTextChange}
                onCreateJourney={onCreateJourney}
                setLoading={setLoading}
              />
            </VStack>
          </Container>
        </Box>
      </Flex>
    </Box>
  )
}

const PreferenceChipsInline = ({ state, onFormTextChange }) => {
  const preferences = {
    learning_style: [
      { value: 'visual', label: 'Visual learner', description: 'I learn best with imagery and visualization' },
      { value: 'action-oriented', label: 'Action-oriented', description: 'I prefer doing and practicing over reflecting' },
      { value: 'reflective', label: 'Reflective', description: 'I like journaling and deep introspection' }
    ],
    task_preference: [
      { value: 'short-actionable', label: 'Short & actionable', description: 'Quick tasks I can complete easily' },
      { value: 'deep-reflection', label: 'Deep reflection', description: 'Longer contemplative practices' }
    ],
    interaction_style: [
      { value: 'gentle', label: 'Gentle approach', description: 'Softer language, lower pressure' },
      { value: 'direct', label: 'Direct approach', description: 'Clear and straightforward guidance' },
      { value: 'structured', label: 'Structured', description: 'Step-by-step with clear frameworks' }
    ]
  }

  const isSelected = (category, value) => {
    return state.goalDetails[category] === value
  }

  const togglePreference = (category, value) => {
    const currentValue = state.goalDetails[category]
    if (currentValue === value) {
      onFormTextChange('goalDetails', category, null)
    } else {
      onFormTextChange('goalDetails', category, value)
    }
  }

  return (
    <VStack spacing={{ base: 4, md: 5 }} align="stretch">
      <Box>
        <Heading size={{ base: 'sm', md: 'md' }} color="gray.800" mb={1}>
          Personalize Your Experience
        </Heading>
        <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
          Optional: Help us tailor exercises to your style
        </Text>
      </Box>

      {Object.entries(preferences).map(([category, options]) => (
        <Box key={category}>
          <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="600" color="gray.700" mb={2}>
            {category === 'learning_style' && 'How do you learn best?'}
            {category === 'task_preference' && 'What task style fits you?'}
            {category === 'interaction_style' && 'What tone works for you?'}
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={{ base: 2, md: 3 }}>
            {options.map((option) => (
              <Card
                key={option.value}
                cursor="pointer"
                onClick={() => togglePreference(category, option.value)}
                bg={isSelected(category, option.value) ? 'brand.50' : 'gray.50'}
                border="2px"
                borderColor={isSelected(category, option.value) ? 'brand.500' : 'gray.200'}
                _hover={{
                  borderColor: isSelected(category, option.value) ? 'brand.600' : 'gray.300',
                  bg: isSelected(category, option.value) ? 'brand.100' : 'gray.100'
                }}
                transition="all 0.2s"
              >
                <CardBody p={{ base: 3, md: 4 }}>
                  <HStack spacing={2} align="start">
                    <Box
                      w="20px"
                      h="20px"
                      borderRadius="full"
                      border="2px"
                      borderColor={isSelected(category, option.value) ? 'brand.500' : 'gray.300'}
                      bg={isSelected(category, option.value) ? 'brand.500' : 'white'}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                    >
                      {isSelected(category, option.value) && (
                        <CheckIcon color="white" boxSize={2.5} />
                      )}
                    </Box>
                    <VStack align="start" spacing={0.5} flex={1}>
                      <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="600" color="gray.800">
                        {option.label}
                      </Text>
                      <Text fontSize={{ base: '2xs', md: 'xs' }} color="gray.600">
                        {option.description}
                      </Text>
                    </VStack>
                  </HStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Box>
      ))}
    </VStack>
  )
}

const GoalTemplates = ({ onFormTextChange }) => {
  const toast = useToast()
  const templates = [
    {
      title: "Critical Thinking",
      goal: "I want to improve my critical thinking and make better decisions under pressure.",
      context: "I often accept information at face value and struggle to analyze situations objectively, especially at work.",
      time: "15 mins per day",
      color: "blue"
    },
    {
      title: "Emotional Intelligence",
      goal: "I want to develop emotional awareness and understand my reactions better.",
      context: "I find myself reacting emotionally without understanding why, and I struggle to recognize patterns in my feelings.",
      time: "15 mins per day",
      color: "green"
    },
    {
      title: "Communication Skills",
      goal: "I want to express my thoughts more clearly and listen more actively.",
      context: "I struggle to articulate my ideas in meetings and often miss what others are really saying.",
      time: "10 mins per day",
      color: "purple"
    },
    {
      title: "Strategic Thinking",
      goal: "I want to think more strategically and see the bigger picture in my decisions.",
      context: "I tend to focus on immediate tasks without considering long-term implications or broader context.",
      time: "15 mins per day",
      color: "pink"
    }
  ]

  const handleTemplateSelect = (template) => {
    
    
    // Use setTimeout to ensure proper state updates
    onFormTextChange('goalDetails', 'goal', template.goal)
    setTimeout(() => {
      onFormTextChange('goalDetails', 'context', template.context)
      setTimeout(() => {
        onFormTextChange('goalDetails', 'time', template.time)
        setTimeout(() => {
          onFormTextChange('goalDetails', 'practiceContext', 'solo')
          setTimeout(() => {
            onFormTextChange('goalDetails', 'availableResources', 'Quiet space at home, pen and paper, smartphone for apps or timers')
          }, 50)
        }, 50)
      }, 50)
    }, 50)
    
    
    
    // Show toast notification after a small delay
    setTimeout(() => {
      toast({
        title: `Template Applied: ${template.title}`,
        description: "Goal details have been filled in below. You can edit them before creating your goal.",
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    }, 200)
    
    // Scroll to form after a brief delay to allow state update
    setTimeout(() => {
      const formElement = document.querySelector('[data-testid="create-goal-form"]')
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 400)
  }

  return (
    <Box>
      <VStack spacing={{ base: 3, md: 4 }} align="stretch">
        <Box textAlign="center">
          <Heading size={{ base: 'xs', md: 'md' }} color="gray.800" mb={1}>
            Quick Start Templates
          </Heading>
          <Text color="gray.600" fontSize={{ base: 'xs', md: 'md' }}>
            Choose a template or create a custom goal below
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 2, md: 2 }} spacing={{ base: 2, md: 4 }}>
          {templates.map((template, index) => (
            <Card
              key={index}
              cursor="pointer"
              _hover={{ shadow: 'md', transform: { base: 'none', md: 'translateY(-2px)' } }}
              _active={{ transform: 'scale(0.98)' }}
              transition="all 0.2s"
              onClick={() => handleTemplateSelect(template)}
              bg="white"
              border="1px"
              borderColor="gray.200"
            >
              <CardBody p={{ base: 2.5, md: 4 }}>
                <VStack align="stretch" spacing={{ base: 1.5, md: 3 }}>
                  <VStack align="stretch" spacing={1}>
                    <Heading size={{ base: '2xs', md: 'sm' }} color="gray.800" noOfLines={1}>
                      {template.title}
                    </Heading>
                    <Badge colorScheme={template.color} variant="subtle" borderRadius="full" fontSize={{ base: '2xs', md: 'xs' }} w="fit-content">
                      {template.time}
                    </Badge>
                  </VStack>
                  <Text fontSize={{ base: '2xs', md: 'sm' }} color="gray.600" noOfLines={2}>
                    {template.goal}
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </VStack>
    </Box>
  )
}

const CreateGoalForm = ({ state, onFormTextChange, onCreateJourney, setLoading }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  

  const validateForm = () => {
    const validation = validateGoalForm(state.goalDetails);
    setValidationErrors(validation.errors);
    return validation.isValid;
  };

  const handleFieldChange = (field, value) => {
    onFormTextChange('goalDetails', field, value);
    
    // Clear specific field error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
    
    // Validate in real-time after first submit attempt
    if (hasAttemptedSubmit) {
      setTimeout(() => {
        validateForm();
      }, 500);
    }
  };

  const handleCreateGoal = async () => {
    setHasAttemptedSubmit(true);
    
    if (!validateForm()) {
      toastService.validationError(
        'Please fix the errors below',
        'All required fields must be filled correctly before creating your goal.'
      );
      return;
    }

    setIsLoading(true);
    try {
      await onCreateJourney();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card
      bg="white"
      borderRadius="xl"
      boxShadow="lg"
      border="1px"
      borderColor="gray.200"
      overflow="hidden"
      data-testid="create-goal-form"
    >
      <CardHeader bg="gradient-to-r" bgGradient="linear(to-r, brand.600, purple.600)" py={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }}>
        <VStack spacing={{ base: 1, md: 2 }}>
          <Heading size={{ base: 'sm', md: 'lg' }} color="white" textAlign="center">
            Define Your Goal
          </Heading>
          <Text color="whiteAlpha.900" textAlign="center" fontSize={{ base: 'xs', md: 'sm' }}>
            Be specific - AI will create personalized daily practices
          </Text>
        </VStack>
      </CardHeader>

      <CardBody p={{ base: 4, md: 8 }}>
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
          <FormControl isRequired isInvalid={validationErrors.goal}>
            <FormLabel fontWeight="600" color="gray.700" fontSize={{ base: 'sm', md: 'md' }}>
              What cognitive skill do you want to develop?
            </FormLabel>
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600" mb={{ base: 2, md: 3 }}>
              Choose a thinking or mental skill—like decision-making, self-awareness, or strategic thinking.
            </Text>
            <Textarea
              placeholder="e.g., I want to improve my critical thinking and question my assumptions..."
              bg="white"
              borderColor={validationErrors.goal ? "red.300" : "gray.300"}
              _hover={{ borderColor: validationErrors.goal ? "red.400" : "gray.400" }}
              _focus={{ borderColor: validationErrors.goal ? "red.500" : "brand.500", boxShadow: validationErrors.goal ? "0 0 0 1px var(--chakra-colors-red-500)" : "0 0 0 1px var(--chakra-colors-brand-500)" }}
              rows={3}
              resize="vertical"
              value={state.goalDetails.goal}
              onChange={(event) => handleFieldChange('goal', event.target.value)}
              fontSize={{ base: 'sm', md: 'md' }}
            />
            {validationErrors.goal && (
              <HStack mt={2} spacing={2}>
                <Icon as={WarningIcon} color="red.500" boxSize={3} />
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="red.600">{validationErrors.goal}</Text>
              </HStack>
            )}
          </FormControl>

          <FormControl isInvalid={validationErrors.context}>
            <FormLabel fontWeight="600" color="gray.700" fontSize={{ base: 'sm', md: 'md' }}>
              Where does this show up? (optional)
            </FormLabel>
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600" mb={{ base: 2, md: 3 }}>
              Describe specific situations where you want to apply this skill.
            </Text>
            <Textarea
              placeholder="e.g., When making decisions at work, I struggle to weigh options objectively..."
              bg="white"
              borderColor={validationErrors.context ? "red.300" : "gray.300"}
              _hover={{ borderColor: validationErrors.context ? "red.400" : "gray.400" }}
              _focus={{ borderColor: validationErrors.context ? "red.500" : "brand.500", boxShadow: validationErrors.context ? "0 0 0 1px var(--chakra-colors-red-500)" : "0 0 0 1px var(--chakra-colors-brand-500)" }}
              rows={2}
              resize="vertical"
              value={state.goalDetails.context}
              onChange={(event) => handleFieldChange('context', event.target.value)}
              fontSize={{ base: 'sm', md: 'md' }}
            />
            {validationErrors.context && (
              <HStack mt={2} spacing={2}>
                <Icon as={WarningIcon} color="red.500" boxSize={3} />
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="red.600">{validationErrors.context}</Text>
              </HStack>
            )}
          </FormControl>

          <FormControl isRequired isInvalid={validationErrors.time}>
            <FormLabel fontWeight="600" color="gray.700" fontSize={{ base: 'sm', md: 'md' }}>
              Daily time commitment
            </FormLabel>
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600" mb={{ base: 2, md: 3 }}>
              How much time can you commit daily?
            </Text>
            <Menu matchWidth>
              <MenuButton
                as={Button}
                rightIcon={<Icon viewBox="0 0 24 24" boxSize={5}>
                  <path fill="currentColor" d="M7 10l5 5 5-5z"/>
                </Icon>}
                bg="white"
                borderColor={validationErrors.time ? "red.300" : "gray.300"}
                borderWidth="1px"
                _hover={{ borderColor: validationErrors.time ? "red.400" : "gray.400" }}
                _active={{ borderColor: validationErrors.time ? "red.500" : "brand.500", boxShadow: validationErrors.time ? "0 0 0 1px var(--chakra-colors-red-500)" : "0 0 0 1px var(--chakra-colors-brand-500)" }}
                textAlign="left"
                fontWeight="normal"
                fontSize={{ base: 'md', md: 'md' }}
                height={{ base: '48px', md: '40px' }}
                w="full"
                color={state.goalDetails.time ? "gray.800" : "gray.400"}
              >
                {state.goalDetails.time ?
                  state.goalDetails.time.replace('mins per day', 'min/day').replace('10 min/day', '10 min/day (Recommended)')
                  : 'Select time'}
              </MenuButton>
              <MenuList
                maxH="300px"
                overflowY="auto"
                zIndex={1500}
                fontSize={{ base: 'md', md: 'md' }}
              >
                <MenuItem
                  onClick={() => handleFieldChange('time', '5 mins per day')}
                  fontSize={{ base: 'md', md: 'md' }}
                  minH="48px"
                  _hover={{ bg: 'brand.50' }}
                >
                  5 min/day
                </MenuItem>
                <MenuItem
                  onClick={() => handleFieldChange('time', '10 mins per day')}
                  fontSize={{ base: 'md', md: 'md' }}
                  minH="48px"
                  _hover={{ bg: 'brand.50' }}
                >
                  10 min/day (Recommended)
                </MenuItem>
                <MenuItem
                  onClick={() => handleFieldChange('time', '15 mins per day')}
                  fontSize={{ base: 'md', md: 'md' }}
                  minH="48px"
                  _hover={{ bg: 'brand.50' }}
                >
                  15 min/day
                </MenuItem>
                <MenuItem
                  onClick={() => handleFieldChange('time', '20 mins per day')}
                  fontSize={{ base: 'md', md: 'md' }}
                  minH="48px"
                  _hover={{ bg: 'brand.50' }}
                >
                  20 min/day
                </MenuItem>
                <MenuItem
                  onClick={() => handleFieldChange('time', '30 mins per day')}
                  fontSize={{ base: 'md', md: 'md' }}
                  minH="48px"
                  _hover={{ bg: 'brand.50' }}
                >
                  30 min/day
                </MenuItem>
              </MenuList>
            </Menu>
            {validationErrors.time && (
              <HStack mt={2} spacing={2}>
                <Icon as={WarningIcon} color="red.500" boxSize={3} />
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="red.600">{validationErrors.time}</Text>
              </HStack>
            )}
          </FormControl>

          <FormControl isRequired isInvalid={validationErrors.practiceContext}>
            <FormLabel fontWeight="600" color="gray.700" fontSize={{ base: 'sm', md: 'md' }}>
              Practice Context
            </FormLabel>
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600" mb={{ base: 2, md: 3 }}>
              Solo or with others?
            </Text>
            <Menu matchWidth>
              <MenuButton
                as={Button}
                rightIcon={<Icon viewBox="0 0 24 24" boxSize={5}>
                  <path fill="currentColor" d="M7 10l5 5 5-5z"/>
                </Icon>}
                bg="white"
                borderColor={validationErrors.practiceContext ? "red.300" : "gray.300"}
                borderWidth="1px"
                _hover={{ borderColor: validationErrors.practiceContext ? "red.400" : "gray.400" }}
                _active={{ borderColor: validationErrors.practiceContext ? "red.500" : "brand.500", boxShadow: validationErrors.practiceContext ? "0 0 0 1px var(--chakra-colors-red-500)" : "0 0 0 1px var(--chakra-colors-brand-500)" }}
                textAlign="left"
                fontWeight="normal"
                fontSize={{ base: 'md', md: 'md' }}
                height={{ base: '48px', md: '40px' }}
                w="full"
                color={state.goalDetails.practiceContext ? "gray.800" : "gray.400"}
              >
                {state.goalDetails.practiceContext === 'solo' ? 'Solo - practice on my own' :
                 state.goalDetails.practiceContext === 'interactive' ? 'Interactive - with others' :
                 'Select context'}
              </MenuButton>
              <MenuList
                maxH="300px"
                overflowY="auto"
                zIndex={1500}
                fontSize={{ base: 'md', md: 'md' }}
              >
                <MenuItem
                  onClick={() => handleFieldChange('practiceContext', 'solo')}
                  fontSize={{ base: 'md', md: 'md' }}
                  minH="48px"
                  _hover={{ bg: 'brand.50' }}
                >
                  Solo - practice on my own
                </MenuItem>
                <MenuItem
                  onClick={() => handleFieldChange('practiceContext', 'interactive')}
                  fontSize={{ base: 'md', md: 'md' }}
                  minH="48px"
                  _hover={{ bg: 'brand.50' }}
                >
                  Interactive - with others
                </MenuItem>
              </MenuList>
            </Menu>
            {validationErrors.practiceContext && (
              <HStack mt={2} spacing={2}>
                <Icon as={WarningIcon} color="red.500" boxSize={3} />
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="red.600">{validationErrors.practiceContext}</Text>
              </HStack>
            )}
          </FormControl>

          <FormControl isInvalid={validationErrors.availableResources}>
            <FormLabel fontWeight="600" color="gray.700" fontSize={{ base: 'sm', md: 'md' }}>
              Available Resources (optional)
            </FormLabel>
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600" mb={{ base: 2, md: 3 }}>
              What do you have access to? (pen, quiet space, etc.)
            </Text>
            <Textarea
              placeholder="e.g., quiet room, pen and paper, park nearby..."
              bg="white"
              borderColor={validationErrors.availableResources ? "red.300" : "gray.300"}
              _hover={{ borderColor: validationErrors.availableResources ? "red.400" : "gray.400" }}
              _focus={{ borderColor: validationErrors.availableResources ? "red.500" : "brand.500", boxShadow: validationErrors.availableResources ? "0 0 0 1px var(--chakra-colors-red-500)" : "0 0 0 1px var(--chakra-colors-brand-500)" }}
              rows={2}
              resize="vertical"
              value={state.goalDetails.availableResources}
              onChange={(event) => handleFieldChange('availableResources', event.target.value)}
              fontSize={{ base: 'sm', md: 'md' }}
            />
            {validationErrors.availableResources && (
              <HStack mt={2} spacing={2}>
                <Icon as={WarningIcon} color="red.500" boxSize={3} />
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="red.600">{validationErrors.availableResources}</Text>
              </HStack>
            )}
          </FormControl>

          <Divider />

          <PreferenceChipsInline state={state} onFormTextChange={onFormTextChange} />

          <Box bg="blue.50" borderRadius="lg" p={{ base: 3, md: 4 }} border="1px" borderColor="blue.200">
            <HStack spacing={{ base: 2, md: 3 }} align="start">
              <Icon as={InfoIcon} color="blue.500" boxSize={{ base: 4, md: 5 }} mt={0.5} />
              <VStack align="start" spacing={0}>
                <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="600" color="blue.800">
                  How it works
                </Text>
                <Text fontSize={{ base: '2xs', md: 'xs' }} color="blue.700">
                  AI creates progressive text-based exercises to build your cognitive skills through reflection and practice.
                </Text>
              </VStack>
            </HStack>
          </Box>

          <Button
            size={{ base: 'md', md: 'lg' }}
            colorScheme="purple"
            leftIcon={<AddIcon />}
            isLoading={isLoading}
            loadingText="Creating..."
            isDisabled={isLoading || (hasAttemptedSubmit && Object.keys(validationErrors).length > 0)}
            onClick={handleCreateGoal}
            w="full"
          >
            Create My Goal
          </Button>
          
          <Text fontSize="xs" color="gray.500" textAlign="center">
            This will create a personalized series of daily practices designed to help you achieve your goal.
          </Text>
        </VStack>
      </CardBody>
    </Card>
  )
}



export default CreateGoalPage